// Endpoints and the layout of a batch folder (LH-F5; the standard layout since LH-L, owner 2026-10-17): a batch is a
// Node service laid out the usual way:
//   package.json              npm start = node src/server.js
//   src/server.js             starts the service (the live container's command)
//   src/app.js                builds it: createApp() from the SDK mounts every endpoint file, nothing is listed by hand
//   src/api/<route>.js        one REST endpoint per file, folder = route (src/lib/manifest.ts); `api/` at the root still works
//   src/services/             the lender client and other helpers, require()d from the endpoints
//   tests/                    the generated test-event files; node_modules and .hub are the sandbox's
// An endpoint is referred to by its route (`/v2/dedupe`): on the canvas, in test events, in captured codes, in the
// API records and in the API catalogue.
import type { CodeFile, HttpMethod, Manifest, ManifestEndpoint } from '@/types'
import { API_ROOT, isApiPath, readDefineApi, routeOf } from '@/lib/manifest'

export const KEEP_FILE = '.gitkeep'
export const isKeep = (path: string) => path === KEEP_FILE || path.endsWith('/' + KEEP_FILE)
export const folderOf = (path: string) => (path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '')
export const baseName = (path: string) => path.split('/').pop() ?? path

/** `/v2/dedupe` → `v2/dedupe` (the short label on the canvas and in lists). */
export const endpointLabel = (route: string) => route.replace(/^\//, '') || '/'
/** `POST /v2/dedupe` */
export const endpointTitle = (ep: Pick<ManifestEndpoint, 'method' | 'path'>) => `${ep.method} ${ep.path}`

export const endpointOf = (m: Manifest | undefined, route: string) => m?.endpoints.find((e) => e.path === route)
export const endpointExists = (m: Manifest | undefined, route: string) => !!endpointOf(m, route)
export const fileOfEndpoint = (m: Manifest | undefined, route: string) => endpointOf(m, route)?.file ?? null

/** The routes the file defines in the working copy (one per file). */
export function routeOfFile(file: CodeFile): string | null {
  if (!isApiPath(file.path) || !/\.(c?js|mjs)$/.test(file.path)) return null
  return readDefineApi(file.content) ? routeOf(file.path) : null
}

/** The endpoint folder this batch uses: `src/api` (standard), or `api` for a batch laid out the old way. */
export const apiRootOf = (files: { path: string }[]) => (files.some((f) => f.path.startsWith('api/')) && !files.some((f) => f.path.startsWith('src/api/')) ? 'api' : API_ROOT)
/** The file a route lives in when it is created: `/v2/soft-offer` → `src/api/v2/soft-offer.js`. */
export const apiFileFor = (files: { path: string }[], route: string) => `${apiRootOf(files)}${route}.js`
/** `v2/get-offer.js` → `src/api/v2/get-offer.js`; keeps a path that is already under an endpoint folder. */
export const apiFilePath = (p: string) => (isApiPath(p) ? p : `${API_ROOT}/${p.replace(/^\/+/, '')}`)

/** The virtual tests file of one endpoint: `/v2/dedupe` → `tests/v2.dedupe.replies.json`. */
export const testsPathOf = (route: string) => `tests/${endpointLabel(route).replace(/\//g, '.')}.replies.json`
export function routeOfTestsPath(path: string, m: Manifest | undefined): string | null {
  if (!path.startsWith('tests/')) return null
  return m?.endpoints.find((e) => testsPathOf(e.path) === path)?.path ?? null
}

// ---- folders ------------------------------------------------------------------------------------------------------

export function foldersOf(files: { path: string }[]): string[] {
  const out = new Set<string>()
  for (const f of files) {
    const parts = f.path.split('/').slice(0, -1)
    for (let i = 1; i <= parts.length; i++) out.add(parts.slice(0, i).join('/'))
  }
  return Array.from(out).sort()
}

export const isValidPath = (p: string) => /^(?!\/)(?!.*\/\/)(?!.*\.\.)[A-Za-z0-9_\-./]+$/.test(p) && !p.endsWith('/')

/** Renames a file or a folder in the working copy; test events follow the endpoint whose route changed. */
export function renamePath<T extends { endpoint: string }>(files: CodeFile[], tests: T[], from: string, to: string): { files: CodeFile[]; tests: T[] } {
  const isFolder = !files.some((f) => f.path === from)
  const move = (p: string) => (isFolder ? (p.startsWith(from + '/') ? to + p.slice(from.length) : p) : p === from ? to : p)
  const routeMap = new Map<string, string>()
  for (const f of files) {
    const np = move(f.path)
    if (np !== f.path && isApiPath(f.path) && isApiPath(np)) routeMap.set(routeOf(f.path), routeOf(np))
  }
  return { files: files.map((f) => ({ ...f, path: move(f.path) })), tests: tests.map((t) => ({ ...t, endpoint: routeMap.get(t.endpoint) ?? t.endpoint })) }
}

// ---- what an endpoint reads (shown on the canvas, never typed by hand) ---------------------------------------------

export function inputsOf(source: string): { applicant: string[]; prev: string[]; env: string[] } {
  const uniq = (re: RegExp) => Array.from(new Set(Array.from(source.matchAll(re), (m) => m[1]!)))
  return { applicant: uniq(/(?:applicant|input)\.(\w+)/g), prev: uniq(/ctx\.prev\.(\w+)/g), env: uniq(/ctx\.env\.([A-Z_][A-Z0-9_]*)|ctx\.secret\(\s*['"`]([A-Z_][A-Z0-9_]*)/g).concat(Array.from(source.matchAll(/ctx\.secret\(\s*['"`]([A-Z_][A-Z0-9_]*)/g), (m) => m[1]!)).filter((x, i, a) => x && a.indexOf(x) === i) }
}

// ---- templates: what the Hub writes into a new batch (the writer may change everything) ---------------------------

const authLine = (auth: string) =>
  auth === 'bearer' ? "Authorization: 'Bearer ' + ctx.env.LENDER_TOKEN, " : auth === 'api_key_header' ? "'X-Api-Key': ctx.secret('LENDER_API_KEY'), " : auth === 'basic' ? "Authorization: 'Basic ' + ctx.secret('LENDER_BASIC_AUTH'), " : ''

/** One endpoint file (Create API, New file → Endpoint). The route comes from the file's place under src/api/. */
export function endpointTemplate(title: string, method: HttpMethod, lenderPath: string, auth: string): string {
  return `const { defineApi } = require('@firstfund/lender-sdk')

/** ${title}: ${method} ${lenderPath} at the lender. Outcomes: ok · error */
module.exports = defineApi({
  title: '${title.replace(/'/g, "\\'")}',
  outcomes: ['ok', 'error'],
  async handler(applicant, ctx) {
    const reply = await ctx.lender({
      method: '${method}',
      url: ctx.env.LENDER_BASE_URL + '${lenderPath}',
      headers: { ${authLine(auth)}'Content-Type': 'application/json' },
      body: { ref: ctx.ref, mobile: applicant.mobile },
      timeoutMs: 8000,
    })
    const b = reply.body || {}
    if (reply.status === 200) return { outcome: 'ok', reply_code: 'OK', lender_ref: b.id }
    return { outcome: 'error', reply_code: 'HTTP_' + reply.status }
  },
})
`
}

export const helperTemplate = (path: string) => `/** ${path}: helpers for this batch (the lender client, formatting). require('${path.startsWith('src/services/') ? '../../services/' : './'}${baseName(path).replace(/\.js$/, '')}') from an endpoint file. */
function digitsOnly(text) {
  return String(text || '').replace(/[^0-9]/g, '')
}
module.exports = { digitsOnly }
`

/** src/server.js: starts the service. `npm start` here, the live container's command, and what `npm run dev` runs in the sandbox. */
export const serverTemplate = () => `// src/server.js: starts the service (npm start; the live container's command). PORT comes from the environment (8080).
const app = require('./app')

app.listen(Number(process.env.PORT || 8080))
`

/** src/app.js: builds the service; every file under src/api is mounted by createApp(), nothing is listed by hand. */
export const appTemplate = () => `// src/app.js: builds the service. Every file under src/api is one REST endpoint (folder = route): src/api/v2/dedupe.js
// is POST /v2/dedupe. createApp() from the SDK mounts them all; nothing is listed by hand. Helpers live in src/services.
const path = require('node:path')
const { createApp } = require('@firstfund/lender-sdk')

module.exports = createApp({ root: path.join(__dirname, '..') })
`

export const packageJsonTemplate = (lenderSlug: string, version: string) => `${JSON.stringify({ name: `${lenderSlug}-batch`, version, private: true, description: `Lender API batch ${lenderSlug}.batchApi.${version}: a Node service, one REST endpoint per file under src/api/`, main: 'src/app.js', scripts: { start: 'node src/server.js', dev: 'node src/server.js', discover: 'hub-discover' }, dependencies: {} }, null, 2)}\n`

export const readmeTemplate = (lenderSlug: string) => `# ${lenderSlug}.batchApi

A Node service written with \`@firstfund/lender-sdk\`:

| Where | What |
|---|---|
| \`src/server.js\` | starts the service (\`npm start\`; the live container runs this) |
| \`src/app.js\` | builds it: \`createApp()\` mounts every endpoint file |
| \`src/api/<route>.js\` | one REST endpoint per file, folder = route: \`src/api/v2/dedupe.js\` is \`POST /v2/dedupe\` |
| \`src/services/\` | the lender client and other helpers the endpoints \`require()\` |
| \`tests/\` | generated: one \`<route>.replies.json\` per endpoint with test events |

Rules: call the lender only through \`ctx.lender(call)\`; no key in the code (\`ctx.env.NAME\` comes from the Env variables
tab); an earlier step's outcome arrives as \`ctx.prev\`. \`.hub/manifest.json\` is generated by Discover and Build.
`

/** The files of a new batch that does not copy an older version. */
export const starterFiles = (lenderSlug: string, version: string): CodeFile[] => [
  { path: 'package.json', content: packageJsonTemplate(lenderSlug, version) },
  { path: 'README.md', content: readmeTemplate(lenderSlug) },
  { path: 'src/server.js', content: serverTemplate() },
  { path: 'src/app.js', content: appTemplate() },
  { path: `${API_ROOT}/${KEEP_FILE}`, content: '' },
  { path: `src/services/${KEEP_FILE}`, content: '' },
]

/** A file name from an endpoint title: "Soft offer" → soft-offer.js (routes are kebab-case, like the owner's examples). */
export function fileNameFor(title: string): string {
  const words = title.replace(/[^a-zA-Z0-9 ]/g, ' ').trim().toLowerCase().split(/\s+/).filter(Boolean)
  return words.length ? words.join('-') + '.js' : ''
}

// ---- the canvas ports -----------------------------------------------------------------------------------------------

/** The outcomes an endpoint can return: what defineApi declares, plus what its test events expect; error always last. */
export function outcomesOf(m: Manifest | undefined, tests: { endpoint: string; expectedOutcome: string }[], route: string): string[] {
  const declared = endpointOf(m, route)?.outcomes ?? []
  const fromTests = tests.filter((t) => t.endpoint === route).map((t) => t.expectedOutcome)
  return [...Array.from(new Set([...declared, ...fromTests])).filter((o) => o !== 'error'), 'error']
}
